import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { SearchAddon } from '@xterm/addon-search'
import { CanvasAddon } from '@xterm/addon-canvas'
import '@xterm/xterm/css/xterm.css'
import { useAuth } from '@clerk/clerk-react'
import { log } from '../lib/logger'
import { GATEWAY_WS } from '../config'

type Status = 'connecting' | 'connected' | 'disconnected'

interface TerminalPanelProps {
  sessionId: string
  label?: string
  onAnalyze: (output: string) => void
  onStatusChange?: (status: Status) => void
}

// ---------------------------------------------------------------------------
// Status dot — used in the header bar
// ---------------------------------------------------------------------------
function StatusDot({ status }: { status: Status }) {
  const color = {
    connected:    '#22c55e',
    connecting:   '#f59e0b',
    disconnected: '#ef4444',
  }[status]
  return (
    <span
      className={status === 'connecting' ? 'animate-pulse' : undefined}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    />
  )
}

export function TerminalPanel({ sessionId, label, onAnalyze, onStatusChange }: TerminalPanelProps) {
  const containerRef    = useRef<HTMLDivElement>(null)
  const termRef         = useRef<Terminal | null>(null)
  const fitAddonRef     = useRef<FitAddon | null>(null)
  const searchAddonRef  = useRef<SearchAddon | null>(null)
  const searchInputRef  = useRef<HTMLInputElement>(null)
  const wsRef           = useRef<WebSocket | null>(null)
  const connectGenRef   = useRef(0)
  const outputBuf       = useRef('')
  const attemptsRef     = useRef(0)
  const firstConnectRef = useRef(true)
  const timerRef        = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectFnRef    = useRef<() => void>(() => {})
  // RTT probe: send {type:'ping', payload:{ts}} every 2s, agent echoes back as
  // pong with the same ts. Measures full keystroke-path latency (browser →
  // gateway → agent → gateway → browser), minus the PTY/shell echo step.
  const pingTimerRef    = useRef<ReturnType<typeof setInterval> | null>(null)

  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  useEffect(() => { getTokenRef.current = getToken })

  const [status, setStatus]         = useState<Status>('connecting')
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [rttMs, setRttMs]           = useState<number | null>(null)
  const [rttGwMs, setRttGwMs]       = useState<number | null>(null)

  useEffect(() => { onStatusChange?.(status) }, [status, onStatusChange])

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus()
    else            termRef.current?.focus()
  }, [searchOpen])

  const connect = useCallback(async () => {
    const term = termRef.current
    if (!term) return

    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }

    const gen = ++connectGenRef.current

    setStatus('connecting')
    log.info('ws_connecting', { session_id: sessionId })

    const token = await getTokenRef.current()

    // A newer connect() call superseded this one during the async gap — abort.
    if (gen !== connectGenRef.current) return

    const wsUrl = token
      ? `${GATEWAY_WS}/ws/browser?session=${sessionId}&token=${encodeURIComponent(token)}`
      : `${GATEWAY_WS}/ws/browser?session=${sessionId}`

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      attemptsRef.current = 0
      setStatus('connected')
      log.info('terminal_connected', { session_id: sessionId })
      // Clear buffered shell prompts on first connect only; keep output on reconnects
      if (firstConnectRef.current) {
        firstConnectRef.current = false
        termRef.current?.clear()
      }

      const dims = fitAddonRef.current?.proposeDimensions()
      if (dims && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ version: '1', type: 'terminal_resize', payload: { cols: dims.cols, rows: dims.rows } }))
        log.debug('terminal_resize', { cols: dims.cols, rows: dims.rows, trigger: 'connect' })
      }
    }

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as { version: string; type: string; payload: unknown }
        if (msg.type === 'terminal_output') {
          const payload = msg.payload as string
          term.write(payload)
          outputBuf.current += payload
          log.debug('ws_message_rx', { type: 'terminal_output', bytes: payload.length })
        } else if (msg.type === 'pong') {
          const ts = (msg.payload as { ts?: number })?.ts
          if (typeof ts === 'number') setRttMs(Date.now() - ts)
        } else if (msg.type === 'pong_gateway') {
          const ts = (msg.payload as { ts?: number })?.ts
          if (typeof ts === 'number') setRttGwMs(Date.now() - ts)
        } else if (msg.type === 'session_end') {
          term.write('\r\n\x1b[33m● Session ended\x1b[0m\r\n')
          log.info('session_end', { session_id: sessionId })
        }
      } catch {
        term.write(event.data as string)
      }
    }

    ws.onerror = () => {
      log.error('ws_error', { session_id: sessionId })
    }

    ws.onclose = () => {
      setStatus('disconnected')
      setRttMs(null)
      setRttGwMs(null)
      const delay = Math.min(1000 * (1 << Math.min(attemptsRef.current, 5)), 30_000)
      attemptsRef.current++
      log.info('ws_disconnected', { session_id: sessionId, reconnect_in_ms: delay, attempt: attemptsRef.current })
      timerRef.current = setTimeout(() => connectFnRef.current(), delay)
    }
  }, [sessionId])

  useLayoutEffect(() => { connectFnRef.current = connect }, [connect])

  useEffect(() => {
    if (!containerRef.current) return
    let mounted = true

    const term = new Terminal({
      allowTransparency: false,
      theme: {
        background:          '#050507',
        foreground:          '#d4d4d4',
        cursor:              '#7c6ff7',
        cursorAccent:        '#050507',
        selectionBackground: '#2d2a4a',
        black:               '#000000',
        red:                 '#cd3131',
        green:               '#0dbc79',
        yellow:              '#e5e510',
        blue:                '#7c6ff7',
        magenta:             '#bc3fbc',
        cyan:                '#11a8cd',
        white:               '#e5e5e5',
        brightBlack:         '#666666',
        brightRed:           '#f14c4c',
        brightGreen:         '#23d18b',
        brightYellow:        '#f5f543',
        brightBlue:          '#a5b4fc',
        brightMagenta:       '#d670d6',
        brightCyan:          '#29b8db',
        brightWhite:         '#e5e5e5',
      },
      fontFamily:      'JetBrains Mono, Fira Code, Cascadia Code, Consolas, monospace',
      fontSize:        14,
      lineHeight:      1.2,
      letterSpacing:   0,
      cursorBlink:     true,
      cursorStyle:     'block',
      scrollback:      10000,
      macOptionIsMeta:  true,
      allowProposedApi: true,
    })

    const fitAddon    = new FitAddon()
    const searchAddon = new SearchAddon()

    term.loadAddon(fitAddon)
    term.loadAddon(searchAddon)
    term.loadAddon(new WebLinksAddon())
    term.open(containerRef.current)

    term.loadAddon(new CanvasAddon())
    void (async () => {
      try {
        const { WebglAddon } = await import('@xterm/addon-webgl')
        if (!mounted) return
        const webgl = new WebglAddon()
        webgl.onContextLoss(() => {
          webgl.dispose()
          if (mounted) term.loadAddon(new CanvasAddon())
        })
        term.loadAddon(webgl)
        log.debug('renderer_webgl_active', {})
      } catch {
        log.debug('renderer_canvas_active', {})
      }
    })()

    fitAddon.fit()
    term.focus()
    termRef.current       = term
    fitAddonRef.current   = fitAddon
    searchAddonRef.current = searchAddon

    // Ctrl+Shift+C/V: copy-paste  |  Ctrl+Shift+F: search  |  Shift+PgUp/Down: scrollback
    term.attachCustomKeyEventHandler((e: KeyboardEvent) => {
      if (e.type !== 'keydown') return true

      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        const sel = term.getSelection()
        if (sel) navigator.clipboard.writeText(sel).catch(() => {})
        return false
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'V') {
        navigator.clipboard.readText().then(text => {
          if (wsRef.current?.readyState === WebSocket.OPEN)
            wsRef.current.send(JSON.stringify({ version: '1', type: 'terminal_input', payload: text }))
        }).catch(() => {})
        return false
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'F') {
        setSearchOpen(o => !o)
        return false
      }
      if (e.ctrlKey && !e.shiftKey && e.key === 'c') {
        if (wsRef.current?.readyState === WebSocket.OPEN)
          wsRef.current.send(JSON.stringify({ version: '1', type: 'terminal_input', payload: '\x03' }))
        return false
      }
      if (e.shiftKey && e.key === 'PageUp')  { term.scrollPages(-1); return false }
      if (e.shiftKey && e.key === 'PageDown') { term.scrollPages(1);  return false }

      return true
    })

    term.onData((data: string) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return
      wsRef.current.send(JSON.stringify({ version: '1', type: 'terminal_input', payload: data }))
      log.trace('ws_message_tx', { type: 'terminal_input', bytes: data.length })
    })

    // RTT probes: send two pings every 2s.
    //   ping_gateway → gateway echoes immediately = browser↔gateway leg only
    //   ping         → agent echoes              = full browser↔agent path
    // The difference (full − gateway) ≈ gateway↔agent leg, which tells us
    // which side to debug when typing feels laggy.
    pingTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const ts = Date.now()
        wsRef.current.send(JSON.stringify({ version: '1', type: 'ping_gateway', payload: { ts } }))
        wsRef.current.send(JSON.stringify({ version: '1', type: 'ping',         payload: { ts } }))
      }
    }, 2000)

    term.onResize(({ cols, rows }: { cols: number; rows: number }) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ version: '1', type: 'terminal_resize', payload: { cols, rows } }))
        log.debug('terminal_resize', { cols, rows, trigger: 'resize_event' })
      }
    })

    connect()

    const ro = new ResizeObserver(() => fitAddonRef.current?.fit())
    ro.observe(containerRef.current)

    return () => {
      mounted = false
      connectGenRef.current++ // invalidate any in-flight connect() awaiting getToken
      if (timerRef.current) clearTimeout(timerRef.current)
      if (pingTimerRef.current) clearInterval(pingTimerRef.current)
      ro.disconnect()
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); wsRef.current = null }
      // WebGL addon has a known dispose bug — suppress to prevent React tree crash
      try { term.dispose() } catch (e) { console.warn('[Terminal] xterm dispose error (suppressed):', e) }
    }
  }, [connect])

  const handleAnalyzeClick = () => {
    const snippet = outputBuf.current.slice(-8192)
    if (snippet.trim()) onAnalyze(snippet)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div
        className="shrink-0 flex items-center justify-between"
        style={{
          height: 48,
          padding: '0 16px',
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="flex items-center gap-2">
          <StatusDot status={status} />
          <span style={{ fontSize: 14, fontWeight: 500, color: '#cccccc', fontFamily: 'inherit' }}>
            {label ?? sessionId}
          </span>
          {(rttGwMs !== null || rttMs !== null) && (
            <span
              title="gateway-only RTT / full agent RTT (agent leg ≈ full − gateway)"
              style={{
                fontSize: 11,
                fontFamily: 'JetBrains Mono, Fira Code, Consolas, monospace',
                marginLeft: 4,
                display: 'inline-flex',
                gap: 4,
              }}
            >
              {rttGwMs !== null && (
                <span style={{ color: rttGwMs < 80 ? '#22c55e' : rttGwMs < 200 ? '#f59e0b' : '#ef4444' }}>
                  gw {rttGwMs}ms
                </span>
              )}
              {rttMs !== null && (
                <span style={{ color: rttMs < 80 ? '#22c55e' : rttMs < 200 ? '#f59e0b' : '#ef4444' }}>
                  / full {rttMs}ms
                </span>
              )}
            </span>
          )}
        </div>

        <button
          onClick={handleAnalyzeClick}
          disabled={status !== 'connected'}
          style={{
            height: 30,
            padding: '0 14px',
            background: 'rgba(124,111,247,0.12)',
            border: '1px solid rgba(124,111,247,0.25)',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            color: '#a5b4fc',
            cursor: status === 'connected' ? 'pointer' : 'not-allowed',
            opacity: status === 'connected' ? 1 : 0.35,
            fontFamily: 'inherit',
            letterSpacing: '0.01em',
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => {
            if (status === 'connected') {
              const b = e.target as HTMLButtonElement
              b.style.background = 'rgba(124,111,247,0.18)'
              b.style.borderColor = 'rgba(124,111,247,0.4)'
            }
          }}
          onMouseLeave={e => {
            const b = e.target as HTMLButtonElement
            b.style.background = 'rgba(124,111,247,0.12)'
            b.style.borderColor = 'rgba(124,111,247,0.25)'
          }}
        >
          Analyze with AI
        </button>
      </div>

      {/* Terminal canvas + search overlay */}
      <div
        className="flex-1 min-h-0 relative"
        style={{ background: '#050507' }}
        onClick={() => termRef.current?.focus()}
      >
        <div ref={containerRef} className="absolute inset-0" />

        {searchOpen && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-focus)',
              borderRadius: 6,
              padding: '6px 8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
            }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value)
                if (e.target.value)
                  searchAddonRef.current?.findNext(e.target.value, { incremental: true })
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') setSearchOpen(false)
                if (e.key === 'Enter') {
                  e.shiftKey
                    ? searchAddonRef.current?.findPrevious(searchTerm)
                    : searchAddonRef.current?.findNext(searchTerm)
                }
              }}
              placeholder="Search…"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text-primary)',
                fontSize: 12,
                width: 160,
                fontFamily: 'inherit',
              }}
            />
            <button
              onClick={() => searchAddonRef.current?.findPrevious(searchTerm)}
              style={searchNavBtnStyle}
              title="Previous (Shift+Enter)"
            >↑</button>
            <button
              onClick={() => searchAddonRef.current?.findNext(searchTerm)}
              style={searchNavBtnStyle}
              title="Next (Enter)"
            >↓</button>
            <button
              onClick={() => setSearchOpen(false)}
              style={{ ...searchNavBtnStyle, marginLeft: 2 }}
            >✕</button>
          </div>
        )}
      </div>
    </div>
  )
}

const searchNavBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontSize: 12,
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 3,
  padding: 0,
  fontFamily: 'inherit',
}
